/**
 * MobX state for the Drive-side `skjv-studies-index.json`. Loads the catalog
 * on app start and re-polls every 6 seconds — but only re-downloads the body
 * when the file's Drive `modifiedTime` actually changed. The last-seen
 * modifiedTime is persisted to localStorage so subsequent app loads don't
 * re-fetch unless the index was touched while we were away.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { loadStorageConfig } from '@skjv/storage';
import {
  fetchStudyIndexIfChanged,
  type StudyIndexEntry,
} from '@/services/study-index-service';

const POLL_INTERVAL_MS = 6_000;
const LS_MODIFIED_KEY = 'skjv-studies-index-modified';

export class StudyIndexState {
  entries: StudyIndexEntry[] = [];
  modifiedTime: string | null = null;
  isLoading = false;
  lastError: string | null = null;

  private timer: number | null = null;
  private inFlight: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this);
    if (typeof window !== 'undefined') {
      this.modifiedTime = window.localStorage.getItem(LS_MODIFIED_KEY);
    }
  }

  start(): void {
    if (typeof window === 'undefined') return;
    if (this.timer !== null) return;
    void this.refresh();
    this.timer = window.setInterval(() => void this.refresh(), POLL_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.doRefresh().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async doRefresh(): Promise<void> {
    const ctx = resolveDriveContext();
    if (!ctx) return;
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      const result = await fetchStudyIndexIfChanged(ctx, this.modifiedTime);
      runInAction(() => {
        this.lastError = null;
        if (result.kind === 'updated') {
          this.entries = result.index.studies;
          this.modifiedTime = result.modifiedTime;
          window.localStorage.setItem(LS_MODIFIED_KEY, result.modifiedTime);
        }
      });
    } catch (err) {
      runInAction(() => {
        this.lastError = (err as Error).message;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}

function resolveDriveContext():
  | { clientId: string; scope: string; skjvFolderId: string }
  | null {
  const config = loadStorageConfig();
  const gd = config.providers['google-drive'];
  if (!gd?.enabled || !gd.clientId || !gd.folderId) return null;
  return { clientId: gd.clientId, scope: gd.scope, skjvFolderId: gd.folderId };
}
